import moment from "moment";
import { API_DATE } from "../Shared/formatters";

// Overtime import (Excel / CSV): file columns, column matching, row checks
// and the template. Rows that pass are sent to
// POST payroll/earning/overtime/import/ as `records` (Overtime Bulk Import
// API). There is no validate (dry-run) endpoint for Overtime, so the server's
// own checks (employee, band, shift, schedule, payroll lock, row limit) are
// only reported after the import, per row.

export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_ACCEPT = ".xlsx,.xls,.csv";

export const BASIS_OPTIONS = [{ value: "shift", label: "Shift" }, { value: "hours", label: "Hours" }];

// `key` is the record field the API takes. Columns can be reordered and
// renamed, so each is matched by its header against the label + aliases.
export const IMPORT_FIELDS = [
  { key: "employee_id", label: "Employee ID", required: true, aliases: ["employee id", "employee code", "emp id", "emp code", "emp no", "employee no", "employee number", "staff id", "staff code"] },
  { key: "date", label: "Date", required: true, aliases: ["date", "ot date", "overtime date", "work date", "worked date", "attendance date", "day", "dt"] },
  { key: "shift", label: "Shift", required: false, aliases: ["shift", "shift name", "worked shift"] },
  { key: "band_name", label: "OT Band", required: false, aliases: ["ot band", "band", "band name", "overtime band"] },
  { key: "basis", label: "Basis", required: false, aliases: ["basis", "pay basis", "calculation basis", "unit", "pay type"] },
  { key: "quantity", label: "Quantity", required: true, aliases: ["quantity", "qty", "credited qty", "credited quantity", "ot hours", "ot hrs", "overtime hours", "hours", "shifts"] },
  { key: "rate", label: "Rate (per unit)", required: true, aliases: ["rate", "rate per unit", "pay rate", "unit rate", "rate per shift", "rate per hour", "ot rate", "price"] },
  { key: "factor", label: "Factor", required: false, aliases: ["factor", "payment factor", "pay factor", "multiplier", "ot factor", "overtime factor"] },
];

// The import's fields. OT Band is left out when bands are off
// (compliance/ is_band_allowed), same as the list and the Add / Edit panel.
export const importFields = (showBand = true) =>
  showBand ? IMPORT_FIELDS : IMPORT_FIELDS.filter((field) => field.key !== "band_name");

export const importGuidelines = (showBand = true) => [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Date format: DD MMM YYYY (e.g. 15 Oct 2026) or YYYY-MM-DD.",
  "Basis is Shift or Hours (blank = Shift). For Hours, Quantity is HH:MM (02:30) or decimal hours (2.5).",
  "Quantity, Rate and Factor are numeric values. Rate is per shift or per hour, as per Basis.",
  showBand
    ? "Leave Shift, OT Band and Factor blank if not applicable: the default band and its factor are used. Amount is calculated by the system."
    : "Leave Shift and Factor blank if not applicable: the default factor is used. Amount is calculated by the system.",
  "Rows go into the payroll cycle chosen on this step, or each row's own cycle when left as Automatic. Maximum file size: 5 MB.",
];

const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const words = (value) => String(value ?? "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// Dice coefficient on letter pairs: 1 = identical, 0 = nothing shared.
const pairSimilarity = (a, b) => {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const pairs = (text) => Array.from({ length: text.length - 1 }, (_, i) => text.slice(i, i + 2));
  const pool = pairs(b);
  const left = pairs(a);
  let shared = 0;
  left.forEach((pair) => {
    const at = pool.indexOf(pair);
    if (at !== -1) { shared += 1; pool.splice(at, 1); }
  });
  return (2 * shared) / (left.length + pairs(b).length);
};

// How well a header names a field, 0..1.
const headerScore = (header, field) => {
  const name = normalize(header);
  if (!name) return 0;
  const headerWords = words(header);
  return [field.label, ...field.aliases].reduce((best, candidate) => {
    const target = normalize(candidate);
    if (name === target) return 1;
    const targetWords = words(candidate);
    const contains = targetWords.length && targetWords.every((word) => headerWords.includes(word))
      ? 0.9 - 0.02 * (headerWords.length - targetWords.length)
      : 0;
    return Math.max(best, contains, pairSimilarity(name, target) * 0.85);
  }, 0);
};

// { [columnIndex]: fieldKey }; each field and column used at most once,
// strongest header matches first.
export const autoMapColumns = (headers, fields = IMPORT_FIELDS) => {
  const mapping = {};
  const usedFields = new Set();
  const usedColumns = new Set();
  headers
    .flatMap((header, index) => fields.map((field) => ({ index, key: field.key, score: headerScore(header, field) })))
    .filter((candidate) => candidate.score >= 0.6)
    .sort((a, b) => b.score - a.score)
    .forEach(({ index, key }) => {
      if (usedFields.has(key) || usedColumns.has(index)) return;
      mapping[index] = key;
      usedFields.add(key);
      usedColumns.add(index);
    });
  return mapping;
};

export const missingRequiredFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

const isBlankCell = (value) => value === null || value === undefined || String(value).trim() === "";
const toNumber = (value) => (isBlankCell(value) ? NaN : Number(String(value).replace(/,/g, "").trim()));

// First non-empty row is the header row; fully blank rows are skipped.
// Data rows are numbered 1, 2, 3... (header and blank rows not counted).
export const splitSheetRows = (sheetRows) => {
  const isBlank = (row) => !row || row.every((cell) => isBlankCell(cell));
  const headerIndex = sheetRows.findIndex((row) => !isBlank(row));
  if (headerIndex === -1) return { headers: [], rows: [] };
  const headers = sheetRows[headerIndex].map((cell) => String(cell ?? "").trim());
  const rows = [];
  sheetRows.slice(headerIndex + 1).forEach((cells) => {
    if (!isBlank(cells)) rows.push({ rowNumber: rows.length + 1, cells });
  });
  return { headers, rows };
};

const DATE_FORMATS = ["DD MMM YYYY", "D MMM YYYY", API_DATE, "DD-MM-YYYY", "DD/MM/YYYY"];
export const parseDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return moment(value).format(API_DATE);
  const parsed = moment(String(value ?? "").trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(API_DATE) : null;
};

// Checks every data row against the mapping (the same rules the API applies
// to the values themselves). Returns { rowNumber, raw: { [fieldKey]: cell },
// errors: [string], record } where `record` is the API record for rows
// without errors.
export const validateImportRows = (rows, mapping) =>
  rows.map(({ rowNumber, cells }) => {
    const raw = {};
    Object.entries(mapping).forEach(([index, key]) => { raw[key] = cells[Number(index)]; });
    return checkImportRow(rowNumber, raw);
  });

// Checks one row's values ({ [fieldKey]: value }). Also used to re-check a
// row corrected in the preview's Edit panel.
export const checkImportRow = (rowNumber, raw) => {
  const errors = [];
  const label = (key) => IMPORT_FIELDS.find((field) => field.key === key).label;
  const record = {};

  if (isBlankCell(raw.employee_id)) errors.push("Employee ID is required");
  else record.employee_id = String(raw.employee_id).trim();

  if (isBlankCell(raw.date)) errors.push("Date is required");
  else {
    const date = parseDate(raw.date);
    if (date) record.date = date;
    else errors.push("Invalid date format");
  }

  if (!isBlankCell(raw.shift)) record.shift = String(raw.shift).trim();
  if (!isBlankCell(raw.band_name)) record.band_name = String(raw.band_name).trim();

  // Basis is optional: blank = Shift (the API's default).
  let basis = "shift";
  if (!isBlankCell(raw.basis)) {
    const match = BASIS_OPTIONS.find((option) => [option.value, option.label].some((text) => normalize(text) === normalize(raw.basis)));
    if (match) { basis = match.value; record.basis = match.value; }
    else errors.push(`Basis "${raw.basis}" is not valid (use Shift or Hours)`);
  }

  // Quantity: number of shifts, or for Hours "HH:MM" / decimal hours.
  if (isBlankCell(raw.quantity)) errors.push("Quantity is required");
  else {
    const text = String(raw.quantity).trim();
    if (basis === "hours" && /^\d{1,3}:[0-5]\d$/.test(text)) record.quantity = text;
    else {
      const number = toNumber(text);
      if (!Number.isFinite(number) || number < 0) {
        errors.push(basis === "hours" ? "Quantity must be HH:MM or a number 0 or more" : "Quantity must be a number 0 or more");
      } else record.quantity = number;
    }
  }

  const numeric = (key, { required, positive = false }) => {
    if (isBlankCell(raw[key])) {
      if (required) errors.push(`${label(key)} is required`);
      return;
    }
    const number = toNumber(raw[key]);
    if (!Number.isFinite(number)) errors.push(`${label(key)} must be a numeric value`);
    else if (positive ? number <= 0 : number < 0) errors.push(`${label(key)} must be ${positive ? "above 0" : "0 or more"}`);
    else record[key] = number;
  };
  numeric("rate", { required: true });
  numeric("factor", { required: false, positive: true });

  return { rowNumber, raw, errors, record: errors.length ? null : record };
};

// Template rows (header + two examples) for the downloadable .xlsx.
export const importTemplateRows = (showBand = true) => {
  const rows = [
    IMPORT_FIELDS.map((field) => field.label),
    ["EMP001", moment().startOf("month").format("DD MMM YYYY"), "Night Shift", "1.5 OT", "Hours", "02:30", 100, 1.5],
    ["EMP002", moment().startOf("month").add(1, "day").format("DD MMM YYYY"), "", "", "Shift", 1, 800, ""],
  ];
  if (showBand) return rows;
  const bandAt = IMPORT_FIELDS.findIndex((field) => field.key === "band_name");
  return rows.map((row) => row.filter((_, index) => index !== bandAt));
};
